import { z } from 'zod';

import type { Role } from '../domain.js';
import { MAX_STORAGE_LIMIT_BYTES, MIN_STORAGE_LIMIT_BYTES } from '../uploads.js';
import { emailSchema } from './auth.js';
import { idSchema, optionalText, paginationQuerySchema } from './common.js';

/**
 * Área da plataforma — o "super admin".
 *
 * Vive fora de qualquer organização. Aqui se cadastram os órgãos que usam o
 * sistema e se administram as pessoas que os acessam. Nada de obra, relatório
 * ou dado operacional: isso é assunto de dentro de cada órgão.
 */
export const createOrganizationBodySchema = z.object({
  name: z.string().trim().min(3, 'Informe o nome da organização').max(160),
  /**
   * Identificador curto na URL. Gerado a partir do nome quando não informado.
   */
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9-]+$/, 'Use apenas letras minúsculas, números e hífen')
    .optional(),
  cnpj: optionalText(20),
  defaultVisitIntervalDays: z.number().int().min(1).max(365).optional(),
  /** Primeiro administrador do órgão. Sem ele, ninguém entra. */
  ownerName: z.string().trim().min(2, 'Informe o nome do responsável').max(120),
  ownerEmail: emailSchema,
});
export type CreateOrganizationBody = z.infer<typeof createOrganizationBodySchema>;

export const updateOrganizationBodySchema = z.object({
  name: z.string().trim().min(3).max(160).optional(),
  cnpj: optionalText(20),
  defaultVisitIntervalDays: z.number().int().min(1).max(365).optional(),
  /**
   * Espaço em nuvem, em bytes.
   *
   * Só a plataforma mexe: é contrato comercial, não configuração do órgão, e
   * por isso não aparece na tela de configurações de dentro dele.
   */
  storageLimitBytes: z
    .number()
    .int()
    .min(MIN_STORAGE_LIMIT_BYTES, 'O mínimo é 1 GB')
    .max(MAX_STORAGE_LIMIT_BYTES, 'O máximo é 10 TB')
    .optional(),
  status: z.enum(['active', 'suspended']).optional(),
});
export type UpdateOrganizationBody = z.infer<typeof updateOrganizationBodySchema>;

export const listOrganizationsQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
});
export type ListOrganizationsQuery = z.infer<typeof listOrganizationsQuerySchema>;

export const listPlatformUsersQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  organizationId: idSchema.optional(),
});
export type ListPlatformUsersQuery = z.infer<typeof listPlatformUsersQuerySchema>;

export const organizationIdParamSchema = z.object({ organizationId: idSchema });

// ─────────────────────────────────────────────────────── respostas

export interface OrganizationEntry {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly cnpj: string | null;
  readonly defaultVisitIntervalDays: number;
  readonly usersCount: number;
  readonly obrasCount: number;
  /** Soma de tudo que a organização guarda: foto, documento, projeto e vídeo. */
  readonly storageUsedBytes: number;
  readonly storageLimitBytes: number;
  readonly createdAt: string;
}

/**
 * Uma pessoa vista da plataforma. `memberships` tem no máximo um item (cada
 * pessoa pertence a um órgão só, ADR 057) e nenhum para quem só administra a
 * plataforma.
 */
export interface PlatformUserEntry {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly status: 'active' | 'invited' | 'suspended';
  readonly isPlatformAdmin: boolean;
  readonly lastLoginAt: string | null;
  readonly memberships: readonly {
    readonly organizationId: string;
    readonly organizationName: string;
    readonly role: Role;
  }[];
}
